














import { debounce, uniqueId } from 'lodash-es'
import AssetDisplay from './AssetDisplay.vue'
import loadingGif from '@/assets/img/loading.gif'
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { assetDataType, shouldProcessTextJson } from '@/utilities/shared-report-helpers.js'
import { processTextJson } from '@/utilities/text-response-helpers.js'
import $ from 'jquery'

export interface Window {
  [key:string]: any; // Add index signature
}

@Component({
  components: {
    AssetDisplay
  }
})
export default class AssetLoader extends Vue {
  @Prop({ required: true }) asset!: any
  @Prop({ required: true }) editable!: any
  @Prop({ required: true }) qualNotable!: any
  @Prop({ required: true }) drop!: any

  loaded:any = false
  fetching:any = false
  uniqueId:any = uniqueId('assetLoader_')
  scrollListener:any = false
  loadingGif:any = loadingGif
  window:Window = window

  @Watch('asset')
  onAssetChange () {
    if (!this.loaded && !this.shouldLoadTextResponses()) {
      this.loaded = true
    }
  }

  mounted () {
    if (!this.shouldLoadTextResponses()) {
      this.loaded = true
    }
    if (this.window.supportsPassive) {
      this.scrollListener = window.addEventListener('scroll', debounce(() => {
        this.calculatePosition()
      }, 300, { trailing: true }), { passive: true })
    } else {
      $(window).on(`scroll.${this.uniqueId}`, debounce(() => {
        this.calculatePosition()
      }, 300, { trailing: true }))
    }
  }

  updateCurrentSlice (sliceName:any) {
    this.$emit('updateCurrentSlice', sliceName)
  }

  shouldLoadTextResponses ():any {
    // Other data types handle their own loading
    if (!['TextSlice', 'Text'].includes(assetDataType(this.asset))) {
      return false
    }

    // Check if responses are already loaded, if not fetch them
    if (assetDataType(this.asset) === 'TextSlice') {
      const sliceObj = this.getTextSliceObj(this.asset, null)
      return !sliceObj.responses || !sliceObj.responses.answers
    } else {
      return !this.asset.responses
    }
  }

  hideTextResponses (responses:any, blacklist:any) {
    responses.map((responseObj: any) => {
      if (responseObj.type === 'answer') {
        if (blacklist.has(responseObj.rid)) {
          responseObj.show = false
        }
      }
      if (responseObj.type === 'group') {
        responseObj.children = this.hideTextResponses(responseObj.children, blacklist)
        return responseObj.children.length
      }
    })
    return responses
  }

  removeTextResponses (responses:any, blacklist:any) {
    return responses.filter((responseObj: any) => {
      if (responseObj.type === 'answer') {
        return !blacklist.has(responseObj.rid)
      }
      if (responseObj.type === 'group') {
        // side effect, mutates children (scary)
        responseObj.children = this.removeTextResponses(responseObj.children, blacklist)
        return responseObj.children.length
      }
    })
  }

  parseTextResponses (responses:any, blacklist:any) {
    if (!blacklist || !blacklist.size) return responses
    if (this.editable) {
      return this.hideTextResponses(responses, blacklist)
    } else {
      // Has side effects
      return this.removeTextResponses(responses, blacklist)
    }
  }

  getTextSliceObj (asset:any, slice:any):any {
    if (slice && asset.textResponses[slice]) {
      return asset.textResponses[slice]
    } else if (asset.textResponses.original) {
      // Default use original slice
      return asset.textResponses.original
    } else {
      const textSlices = Object.keys(asset.textResponses)
      // default to first one if no original slice
      return asset.textResponses[textSlices[0]]
    }
  }

  setResponsesOnTextSliceObj (asset:any, slice:any, responses:any) {
    this.$set(this.getTextSliceObj(asset, slice), 'responses', responses)
  }

  calculatePosition () {
    if (this.fetching || this.loaded || !this.$refs.loading) {
      return
    }
    const bodyRect = document.body.getBoundingClientRect()
    const loadingRefs:any = this.$refs.loading
    const elemRect = loadingRefs.getBoundingClientRect()
    const offset = elemRect.top - bodyRect.top

    const doc:any = $(document)
    if (doc.scrollTop() + window.innerHeight >= offset) {
      this.loadTextResponses()
      if (this.scrollListener) {
        this.window.removeEventListener(this.scrollListener)
      } else {
        $(window).off(`scroll.${this.uniqueId}`)
      }
    }
  }

  loadTextResponses () {
    if (assetDataType(this.asset) === 'TextSlice') {
      // don't mark as loaded until we've loaded ALL of the text responses
      const loadTextPromises:any = []
      var ridBlacklist:any = []
      if (this.asset.ridBlacklist) {
        ridBlacklist = new Set(Object.keys(this.asset.ridBlacklist).length ? [...this.asset.ridBlacklist] : [])
      }
      // If we move back to only loading one slice at a time, we'll have to change some logic in asset-display to reflect this
      Object.keys(this.asset.textResponses).forEach((slice:any) => {
        const textSliceObj = this.getTextSliceObj(this.asset, slice)

        // Slices have their own asset type now since we mix images + text responses
        if (textSliceObj.asset_type === 'viz-slice') return

        loadTextPromises.push(new Promise((resolve, reject) => {
          this.fetchTextResponses(textSliceObj.responsesURL, (responses:any) => {
            const asset = Object.assign({}, this.asset)
            responses.answers = this.parseTextResponses(responses.answers, ridBlacklist)
            this.setResponsesOnTextSliceObj(asset, slice, responses)
            this.$emit('updateAsset', asset)
            resolve()
          })
        }))
      })
      Promise.all(loadTextPromises).then((data:any) => {
        this.loaded = true
      })
    } else {
      this.fetchTextResponses(this.asset.responsesURL, (responses:any) => {
        const asset = Object.assign({}, this.asset, { responses })
        this.$emit('updateAsset', asset)
      })
    }
  }

  fetchTextResponses (url:any, callback:any) {
    this.fetching = true

    fetch(url)
      .then(res => res.json())
      .then(responses => {
        if (shouldProcessTextJson(this.asset)) {
          responses = processTextJson(responses)
        }

        callback(responses)
      })
      .catch(err => console.error(err))
      .then(() => { this.fetching = false })
  }
}
